import { createContext, useContext, ReactNode } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useIsDexEnabled } from '../data-exchange/use-dex-access-guard';

export type FederatedSearchRemoteState = 'checking' | 'available' | 'unavailable';

const FederatedSearchRemoteStateContext = createContext<FederatedSearchRemoteState>('available');

export const FederatedSearchRemoteStateProvider = ({
    federatedSearchRemoteState,
    children,
}: {
    federatedSearchRemoteState: FederatedSearchRemoteState;
    children: ReactNode;
}) => (
    <FederatedSearchRemoteStateContext.Provider value={federatedSearchRemoteState}>
        {children}
    </FederatedSearchRemoteStateContext.Provider>
);

export const useFederatedSearchRemoteState = () => useContext(FederatedSearchRemoteStateContext);

/** Not confirmed available (still checking, or confirmed unavailable)*/
export function useIsFederatedSearchEnabledButNotAvailable(): boolean {
    const { dexNextEnabled } = useEnabledFeatures();
    const federatedSearchRemoteState = useFederatedSearchRemoteState();
    return dexNextEnabled && federatedSearchRemoteState !== 'available';
}

/** Confirmed down — never true during the initial probe ('checking').*/
export function useIsFederatedSearchEnabledButConfirmedUnavailable(): boolean {
    const { dexNextEnabled } = useEnabledFeatures();
    const federatedSearchRemoteState = useFederatedSearchRemoteState();
    return dexNextEnabled && federatedSearchRemoteState === 'unavailable';
}

/** DEx is enabled and, when Federated Search applies, it's confirmed available. */
export function useIsDexFullyOperational(): boolean {
    const { isDexEnabled } = useIsDexEnabled();
    const isFederatedSearchEnabledButNotAvailable = useIsFederatedSearchEnabledButNotAvailable();
    return isDexEnabled && !isFederatedSearchEnabledButNotAvailable;
}

/** Still probing the remote — not yet confirmed available or unavailable. */
export function useIsFederatedSearchEnabledButCheckingAvailability(): boolean {
    const { dexNextEnabled } = useEnabledFeatures();
    const federatedSearchRemoteState = useFederatedSearchRemoteState();
    return dexNextEnabled && federatedSearchRemoteState === 'checking';
}
